
import React, { useEffect, useState } from 'react';
import { getStats, getQuestions, getTagStats } from '../services/storageService';
import { QuestionCategory } from '../types';
import { TrendingUp, Book, Loader2, GraduationCap, Calendar, History, CalendarDays, PenTool, BarChart3, Tag, Filter, PlayCircle, BrainCircuit } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from 'recharts';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<any>(null);
  const [practiceDist, setPracticeDist] = useState<any[]>([]);
  
  // Tag Stats State
  const [tagStats, setTagStats] = useState<{name: string, count: number}[]>([]);
  const [tagTimeFilter, setTagTimeFilter] = useState<'today' | '3days' | 'week'>('week');
  const [tagCategoryFilter, setTagCategoryFilter] = useState<string>('All');

  useEffect(() => {
    const load = async () => {
      const data = await getStats();
      setStats(data);

      // Calculate Practice Count Distribution
      const allQuestions = await getQuestions();
      const counts = [0, 0, 0, 0, 0, 0]; // 0, 1, 2, 3, 4, 5+
      
      allQuestions.forEach(q => {
          if (q.deletedAt) return;
          const totalPractice = (q.mistakeCount || 0) + (q.correctCount || 0);
          const idx = Math.min(totalPractice, 5);
          counts[idx]++;
      });

      const distData = counts.map((count, i) => ({
          name: i === 5 ? '5+次' : `${i}次`,
          count: count
      }));
      setPracticeDist(distData);
    };
    load();
  }, []);

  useEffect(() => {
      const loadTags = async () => {
          const data = await getTagStats(tagTimeFilter, tagCategoryFilter);
          setTagStats(data);
      };
      loadTags();
  }, [tagTimeFilter, tagCategoryFilter]);

  if (!stats) return <div className="flex h-64 items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2"/> 加载数据中...</div>;

  const StatCard = ({ icon, label, value, color, action }: any) => (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between transition-transform hover:scale-[1.02]">
      <div className="flex items-center space-x-4">
          <div className={`p-4 rounded-xl ${color} bg-opacity-10 text-opacity-100`}>
            {React.cloneElement(icon, { className: `text-${color.split('-')[1]}-600` })}
          </div>
          <div>
            <p className="text-slate-500 text-sm font-medium">{label}</p>
            <p className="text-2xl font-bold text-slate-800">{value}</p>
          </div>
      </div>
      {action && <div>{action}</div>}
    </div>
  );

  // Custom Tooltip for Tag Stats
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-xl shadow-xl border border-gray-100 text-sm">
          <p className="font-bold text-slate-700 mb-1">{label}</p>
          <p className="text-rose-500 font-medium">
             错题数量: {payload[0].value}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">学习概览</h1>
          <p className="text-slate-500 mt-1">坚持复习，积少成多。</p>
        </div>
        <div className="text-sm text-slate-400">同步状态: {(import.meta as any).env?.PROD ? 'Cloud' : 'Local'}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard icon={<Book size={24} />} label="总错题" value={stats.total} color="bg-blue-500" />
        
        {/* Smart Review Card */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between transition-transform hover:scale-[1.02] relative overflow-hidden group">
            <div className="absolute right-0 top-0 bottom-0 w-1 bg-amber-400"></div>
            <div className="flex items-center space-x-4">
                <div className="p-4 rounded-xl bg-amber-500 bg-opacity-10">
                    <BrainCircuit size={24} className="text-amber-600" />
                </div>
                <div>
                    <p className="text-slate-500 text-sm font-medium">今日待复习</p>
                    <p className="text-2xl font-bold text-slate-800">{stats.dueCount}</p>
                </div>
            </div>
            {stats.dueCount > 0 && (
                <button 
                    onClick={() => navigate('/practice', { state: { mode: 'smart_review' } })}
                    className="p-2 bg-amber-100 text-amber-600 rounded-full hover:bg-amber-200 transition-colors shadow-sm animate-pulse"
                    title="开始智能复习"
                >
                    <PlayCircle size={24} />
                </button>
            )}
        </div>

        <StatCard icon={<GraduationCap size={24} />} label="已掌握" value={stats.masteredCount} color="bg-green-500" />
        <StatCard icon={<PenTool size={24} />} label="今日刷题" value={stats.todayPracticeCount} color="bg-purple-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tag Stats Chart (Top 5 Weakness Tags) */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 lg:col-span-2 flex flex-col">
              <div className="flex justify-between items-start md:items-center mb-6 flex-col md:flex-row gap-4">
                  <h3 className="font-bold text-slate-700 flex items-center">
                      <Tag size={20} className="mr-2 text-rose-500"/> 高频薄弱考点 (Top 5)
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-2">
                       <select 
                          value={tagCategoryFilter}
                          onChange={(e) => setTagCategoryFilter(e.target.value)}
                          className="px-2 py-1.5 text-xs font-bold rounded-lg border border-gray-200 bg-gray-50 text-slate-600 outline-none focus:ring-2 focus:ring-rose-200"
                        >
                            <option value="All">全部分类</option>
                            {Object.values(QuestionCategory).map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      <div className="flex bg-gray-100 p-1 rounded-lg self-start">
                          {[{k:'today', l:'今天'}, {k:'3days', l:'三天内'}, {k:'week', l:'一周内'}].map(opt => (
                              <button 
                                key={opt.k}
                                onClick={() => setTagTimeFilter(opt.k as any)}
                                className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${tagTimeFilter === opt.k ? 'bg-white text-rose-500 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                              >
                                  {opt.l}
                              </button>
                          ))}
                      </div>
                  </div>
              </div>
              
              <div className="flex-1 min-h-[250px] w-full relative">
                  {tagStats.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={tagStats} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                              <defs>
                                <linearGradient id="colorCount" x1="0" y1="0" x2="1" y2="0">
                                  <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.8}/>
                                  <stop offset="95%" stopColor="#fb7185" stopOpacity={0.6}/>
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                              <XAxis type="number" hide />
                              <YAxis 
                                dataKey="name" 
                                type="category" 
                                width={80} 
                                tick={{fontSize: 12, fill: '#64748b', fontWeight: 500}} 
                                axisLine={false} 
                                tickLine={false} 
                              />
                              <Tooltip content={<CustomTooltip />} cursor={{fill: '#fff1f2', opacity: 0.5}} />
                              <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={20} animationDuration={1000}>
                                  {tagStats.map((entry, index) => (
                                      // Top 1 gets a stronger red
                                      <Cell key={`cell-${index}`} fill={index === 0 ? '#e11d48' : 'url(#colorCount)'} />
                                  ))}
                              </Bar>
                          </BarChart>
                      </ResponsiveContainer>
                  ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                          <Tag size={32} className="mb-2 opacity-20" />
                          <p className="text-sm font-medium">该范围内暂无高频错题</p>
                          <p className="text-xs opacity-70 mt-1">尝试切换时间范围或分类</p>
                      </div>
                  )}
              </div>
          </div>

          <div className="grid grid-cols-1 gap-6">
             <StatCard 
                icon={<Calendar size={24} />} 
                label="今日新增" 
                value={stats.todayMistakes} 
                color="bg-orange-500" 
                action={stats.todayMistakes > 0 && (
                    <button 
                        onClick={() => navigate('/review-today')}
                        className="p-2 bg-orange-100 text-orange-600 rounded-full hover:bg-orange-200 transition-colors shadow-sm"
                        title="回顾今日错题"
                    >
                        <PlayCircle size={24} />
                    </button>
                )}
             />
             <StatCard icon={<History size={24} />} label="昨日新增" value={stats.yesterdayMistakes} color="bg-slate-500" />
             <StatCard icon={<TrendingUp size={24} />} label="近一周新增" value={stats.weekMistakes} color="bg-indigo-500" />
          </div>

          {/* Practice Frequency Chart */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 lg:col-span-3">
              <h3 className="font-bold text-slate-700 mb-6 flex items-center">
                  <BarChart3 size={20} className="mr-2 text-indigo-500"/> 题目复习频率分布
              </h3>
              <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={practiceDist} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                          <defs>
                            <linearGradient id="colorPractice" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8}/>
                              <stop offset="95%" stopColor="#818cf8" stopOpacity={0.6}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                          <XAxis dataKey="name" tick={{fontSize: 12, fill: '#94a3b8'}} axisLine={false} tickLine={false} />
                          <YAxis tick={{fontSize: 12, fill: '#94a3b8'}} axisLine={false} tickLine={false} />
                          <Tooltip 
                            cursor={{fill: '#eef2ff'}}
                            contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'}}
                          />
                          <Bar dataKey="count" radius={[6, 6, 0, 0]} barSize={40} animationDuration={1000}>
                              {practiceDist.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={index === 0 ? '#fbbf24' : 'url(#colorPractice)'} />
                              ))}
                          </Bar>
                      </BarChart>
                  </ResponsiveContainer>
              </div>
              <p className="text-center text-xs text-slate-400 mt-4 flex items-center justify-center">
                 <span className="w-2 h-2 rounded-full bg-amber-400 mr-2"></span> 黄色代表从未复习过的题目，建议优先处理
              </p>
          </div>
      </div>
    </div>
  );
};

export default Dashboard;
